// Backend tracking utility for sending analytics events to the tracking API
// - Runs independently alongside GA4
// - Buffers failed requests for retry
// - SSR-safe: all browser APIs are guarded

type TrackingContext = {
  operatorSlug: string
  sessionId: string
  fingerprint: string
  utmParams: Record<string, string>
}

let context: TrackingContext | null = null
let isInitialized = false
let failedBuffer: Array<() => void> = []

const API_BASE = import.meta.env.VITE_API_BASE || ''
const TRACKING_PATH = '/public/tracking/event'
const SESSION_STORAGE_KEY = 'tripz_session_id'

/**
 * Generate a UUID using crypto.randomUUID with fallback
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Get or create session ID stored in sessionStorage
 */
function getSessionId(): string {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return generateUUID()
  }
  let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY)
  if (!sessionId) {
    sessionId = generateUUID()
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId)
    } catch {
      // Ignore storage errors (e.g., private browsing)
    }
  }
  return sessionId
}

/**
 * Generate a lightweight browser fingerprint from stable browser properties
 */
function generateFingerprint(): string {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return ''
  }
  const components = [
    screen.width,
    screen.height,
    screen.colorDepth,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.language,
    navigator.platform,
    navigator.hardwareConcurrency || 0,
  ]
  const raw = components.join('|')
  // Simple hash for fingerprint
  let hash = 0
  for (let i = 0; i < raw.length; i++) {
    const char = raw.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36)
}

/**
 * Extract UTM parameters from the current URL
 */
function extractUtmParams(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {}
  }
  const params: Record<string, string> = {}
  const searchParams = new URLSearchParams(window.location.search)
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
  for (const key of utmKeys) {
    const value = searchParams.get(key)
    if (value) {
      params[key] = value
    }
  }
  return params
}

/**
 * Send tracking event to the backend API
 */
async function sendEvent(eventType: string, additionalData: Record<string, unknown> = {}): Promise<void> {
  if (!API_BASE || !context) {
    return
  }

  const payload = {
    event_type: eventType,
    operator_slug: context.operatorSlug,
    session_id: context.sessionId,
    fingerprint: context.fingerprint,
    page_url: typeof window !== 'undefined' ? window.location.href : '',
    referrer: typeof document !== 'undefined' ? document.referrer : '',
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    timestamp: new Date().toISOString(),
    ...context.utmParams,
    ...additionalData,
  }

  try {
    const response = await fetch(`${API_BASE}${TRACKING_PATH}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      throw new Error(`Tracking request failed: ${response.status}`)
    }
  } catch (error) {
    // Buffer failed request for retry
    failedBuffer.push(() => sendEvent(eventType, additionalData))
    // Log in development only
    if (import.meta.env.DEV) {
      console.warn('[backendTracking] Failed to send event:', eventType, error)
    }
  }
}

/**
 * Retry failed requests (called periodically or on visibility change)
 */
function retryFailedRequests(): void {
  if (failedBuffer.length === 0) return
  const queue = failedBuffer.slice()
  failedBuffer = []
  for (const fn of queue) {
    fn()
  }
}

/**
 * Initialize backend tracking with operator context
 */
export function initBackendTracking(args: { operatorSlug: string }): void {
  if (isInitialized) return
  if (typeof window === 'undefined') return

  context = {
    operatorSlug: args.operatorSlug,
    sessionId: getSessionId(),
    fingerprint: generateFingerprint(),
    utmParams: extractUtmParams(),
  }

  isInitialized = true

  // Retry failed requests when page becomes visible
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      retryFailedRequests()
    }
  })
}

/**
 * Track homepage page view
 */
export function trackPageView(): void {
  if (typeof window === 'undefined') return
  sendEvent('page_view', { source: 'mini_site' })
}

/**
 * Track trip detail view
 */
export function trackTripView(tripSlug: string): void {
  if (typeof window === 'undefined') return
  sendEvent('trip_view', { trip_slug: tripSlug, source: 'mini_site' })
}

/**
 * Track trip contact button click
 */
export function trackTripClick(tripSlug: string): void {
  if (typeof window === 'undefined') return
  sendEvent('trip_click', { trip_slug: tripSlug, source: 'mini_site' })
}

/**
 * Track CTA click (general contact).
 *
 * `ctaType` types the click so WhatsApp / Call / Book / Callback / Inquiry are
 * distinguishable in the backend `cta_click` event — previously every mini-site
 * CTA fired an untyped event and they were indistinguishable. Backward
 * compatible: the param is optional, so existing untyped callers are unchanged.
 * Forwarded as `cta_type`, which TripzOperator's tracking DTO already accepts.
 */
export function trackCtaClick(tripSlug?: string, ctaType?: string): void {
  if (typeof window === 'undefined') return
  sendEvent('cta_click', {
    source: 'mini_site',
    ...(tripSlug ? { trip_slug: tripSlug } : {}),
    ...(ctaType ? { cta_type: ctaType } : {}),
  })
}

// ── Balance pay link (operator-shared payment page) ──────────────────────────
// The traveller-facing funnel: view → start → success, plus the exits.
// Without these, drop-off on a money page is invisible.
//
// Every name here MUST exist in the server's CreateTrackingEventDto enum, which
// is validated in TWO separate arrays. An event missing from either is dropped
// at the boundary with a 400 the client's .catch swallows — invisible loss.

export type PayLinkEventData = {
  amount_minor?: number
  booking_ref?: string
  /** Why the link could not be used: invalid | expired | paid | offline. */
  reason?: string
}

/** The page rendered a usable link. Denominator for the whole funnel. */
export function trackPayLinkView(d: PayLinkEventData): void {
  if (typeof window === 'undefined') return
  sendEvent('pay_link_view', { source: 'mini_site', ...d })
}

/** Resolve failed — `reason` is the bounce cause, which is the useful part. */
export function trackPayLinkUnavailable(reason: string): void {
  if (typeof window === 'undefined') return
  sendEvent('pay_link_unavailable', { source: 'mini_site', reason })
}

/** Checkout opened. view → start is the "did the page convince them" step. */
export function trackPayLinkStart(d: PayLinkEventData): void {
  if (typeof window === 'undefined') return
  sendEvent('pay_link_start', { source: 'mini_site', ...d })
}

export function trackPayLinkSuccess(d: PayLinkEventData): void {
  if (typeof window === 'undefined') return
  sendEvent('pay_link_success', { source: 'mini_site', ...d })
}

/** Checkout could not open, or the payment was declined. */
export function trackPayLinkFailed(d: PayLinkEventData): void {
  if (typeof window === 'undefined') return
  sendEvent('pay_link_failed', { source: 'mini_site', ...d })
}

/** Sheet dismissed without paying — distinct from a failure, and the biggest
 *  drop-off bucket in most checkout funnels. */
export function trackPayLinkAbandon(d: PayLinkEventData): void {
  if (typeof window === 'undefined') return
  sendEvent('pay_link_abandon', { source: 'mini_site', ...d })
}
